import type { Automation, AutomationTrigger, FlowDocument } from '@emailforge/schemas';

import { apiSlice } from '@/features/api/apiSlice';

export type AutomationRun = {
  id: string;
  status: 'queued' | 'running' | 'success' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  n8nExecutionId: string | null;
  error: unknown;
};

export const automationsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    listAutomations: b.query<Automation[], void>({
      query: () => '/automations',
      providesTags: ['Automation'],
    }),
    listAutomationRuns: b.query<AutomationRun[], string>({
      query: (id) => `/automations/${id}/runs`,
      providesTags: (_r, _e, id) => [{ type: 'Automation', id: `${id}:runs` }],
    }),
    createAutomation: b.mutation<
      { id: string },
      { name: string; triggerType: AutomationTrigger; templateId: string; config?: Record<string, unknown> }
    >({
      query: (body) => ({ url: '/automations', method: 'POST', body: { ...body, config: body.config ?? {} } }),
      invalidatesTags: ['Automation'],
    }),
    toggleAutomation: b.mutation<{ ok: true }, { id: string; active: boolean }>({
      query: ({ id, active }) => ({ url: `/automations/${id}/toggle`, method: 'POST', body: { active } }),
      invalidatesTags: ['Automation'],
    }),
    deleteAutomation: b.mutation<void, string>({
      query: (id) => ({ url: `/automations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Automation'],
    }),
    getAutomationFlow: b.query<FlowDocument, string>({
      query: (id) => `/automations/${id}/flow`,
      providesTags: (_r, _e, id) => [{ type: 'Automation', id }],
    }),
    saveAutomationFlow: b.mutation<
      { flow: FlowDocument; n8nSynced: boolean },
      { id: string; flow: FlowDocument }
    >({
      query: ({ id, flow }) => ({ url: `/automations/${id}/flow`, method: 'POST', body: flow }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Automation', id }],
    }),
  }),
});

export const {
  useListAutomationsQuery,
  useListAutomationRunsQuery,
  useCreateAutomationMutation,
  useToggleAutomationMutation,
  useDeleteAutomationMutation,
  useGetAutomationFlowQuery,
  useSaveAutomationFlowMutation,
} = automationsApi;
